import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import getCardTemplatesQuery from '../../../graphql/queries/getCardTemplates';
import Button from '../../../../../components/global/Button';
import { CARD_SIDE } from '../../../../../services/cards/constants';
import useCardElementConfiguration from './useCardElementConfiguration';
import useCardCreateRoutine from './useCardCreateRoutine';
import CardCategories from './components/CardCategories';
import getCardGroupsQuery from '../../../graphql/queries/getCardGroups';
import CardSideGroupSettings from './components/CardSideGroupSettings';
import Alert from '../../../../../components/global/Alert';
import {
  getCardElementsFromCardTemplates,
  getDefaultCardSideGroups,
} from './helpers';

const propTypes = {
  cardTemplates: PropTypes.shape({
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    ),
  }),
  cardGroups: PropTypes.shape({
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    ),
  }),
};

const CardCreate = ({ cardTemplates, cardGroups }) => {
  const [selectedCardGroup, setSelectedCardGroup] = useState(null);
  const [selectedCardSideGroups, setSelectedCardSideGroups] = useState(
    getDefaultCardSideGroups(),
  );
  const [selectedCategories, setSelectedCategories] = useState([]);
  const {
    elementConfigurations,
    onElementConfigurationChange,
    getElementConfigurationStateById,
  } = useCardElementConfiguration(
    getCardElementsFromCardTemplates(cardTemplates?.records ?? []),
    cardTemplates?.records,
  );

  const { onCreate, loading, error } = useCardCreateRoutine({
    selectedCardSideGroups,
    selectedCardGroup,
    selectedCategories,
    cardTemplates: cardTemplates?.records,
    cardElementConfigurationStates: elementConfigurations,
  });

  const frontCardSideGroups = selectedCardSideGroups.filter(
    cardSideGroup => cardSideGroup.side === CARD_SIDE.FRONT,
  );
  const backCardSideGroups = selectedCardSideGroups.filter(
    cardSideGroup => cardSideGroup.side === CARD_SIDE.BACK,
  );

  const onCardSideGroupChange = (id, value) => {
    setSelectedCardSideGroups(prev => {
      return prev.map(selectedCardSideGroup => {
        if (selectedCardSideGroup.id === id) {
          return {
            ...selectedCardSideGroup,
            ...value,
          };
        }

        return selectedCardSideGroup;
      });
    });
  };

  return (
    <div className="card-create-wrap">
      {cardGroups && (
        <Fragment>
          <h5>Select a CardGroup:</h5>

          <div className="d-flex">
            {cardGroups.records.map(cardGroup => {
              return (
                <Button
                  className="mr-1"
                  key={cardGroup.id}
                  onClick={() => setSelectedCardGroup(cardGroup)}
                  btnOutline={selectedCardGroup?.id !== cardGroup.id}
                >
                  {cardGroup.name}
                </Button>
              );
            })}
          </div>

          <hr />
        </Fragment>
      )}

      {cardTemplates && (
        <Fragment>
          <h3>Front CardSideGroups:</h3>
          {frontCardSideGroups.map(cardSideGroup => {
            return (
              <CardSideGroupSettings
                key={cardSideGroup.name}
                cardSideGroup={cardSideGroup}
                cardTemplates={cardTemplates.records}
                onChange={value =>
                  onCardSideGroupChange(cardSideGroup.id, value)
                }
                getElementConfigurationStateById={
                  getElementConfigurationStateById
                }
                onElementConfigurationChange={onElementConfigurationChange}
              />
            );
          })}

          <h3>Back CardSideGroups:</h3>
          {backCardSideGroups.map(cardSideGroup => {
            return (
              <CardSideGroupSettings
                key={cardSideGroup.name}
                cardSideGroup={cardSideGroup}
                cardTemplates={cardTemplates.records}
                onChange={value =>
                  onCardSideGroupChange(cardSideGroup.id, value)
                }
                getElementConfigurationStateById={
                  getElementConfigurationStateById
                }
                onElementConfigurationChange={onElementConfigurationChange}
              />
            );
          })}
        </Fragment>
      )}

      <hr />

      <div className="mt-3">
        <h5>Select Categories for your template:</h5>

        <CardCategories
          onCategorySelect={cardCategory => {
            setSelectedCategories(prev => {
              if (prev.find(c => c.id === cardCategory.id)) {
                return prev.filter(c => c.id !== cardCategory.id);
              }

              return [...prev, cardCategory];
            });
          }}
          selectedCategories={selectedCategories}
        />
      </div>

      <hr />

      {error && <Alert type="danger">{error}</Alert>}

      <div className="text-right">
        <Button disabled={loading} onClick={onCreate}>
          {loading ? 'Creating...' : 'Create Card'}
        </Button>
      </div>
    </div>
  );
};

CardCreate.propTypes = propTypes;

const withCardTemplates = graphql(getCardTemplatesQuery, {
  options: () => ({
    variables: {
      offset: 0,
      limit: 9999,
    },
  }),
  props: ({ data: { cardTemplates } }) => ({
    cardTemplates,
  }),
});

const withCardGroups = graphql(getCardGroupsQuery, {
  options: () => ({
    variables: {
      offset: 0,
      limit: 9999,
    },
  }),
  props: ({ data: { cardGroups } }) => ({
    cardGroups,
  }),
});

export default compose(withCardTemplates, withCardGroups)(CardCreate);
